/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import Filter from 'bad-words';
import moment from 'moment';
import CommentModerationStatus from '@linc-technologies/linced-common/enums/comment-moderation-status';

export default class CommentsController extends Controller {
  @action
  setFilter(value) {
    this.filter = value;
  }

  queryParams = ['status'];

  @service
  session;

  @service
  dates;

  @service
  heroApi;

  @service
  router;

  @service
  toaster;

  CommentModerationStatus = CommentModerationStatus;
  safeWords = ['hui', 'god'];
  @tracked
  declineAllCommentDialogOpen = false;
  @tracked
  approveAllCommentDialogOpen = false;
  @tracked filter = 'day';
  @tracked status = CommentModerationStatus.UNSPECIFIED;

  get statusLabel() {
    switch (this.status) {
      case CommentModerationStatus.ACCEPTED:
        return 'approved';
      case CommentModerationStatus.DECLINED:
        return 'declined';
    }
    return 'pending';
  }

  @computed('filter')
  get commentStartTime() {
    switch (this.filter) {
      case 'day':
        return moment().subtract(1, 'day').unix();
      case 'month':
        return moment().subtract(1, 'month').unix();
      case 'year':
        return moment(this.dates.rolloverDate).unix();
      default:
        return moment().subtract(14, 'days').unix();
    }
  }

  @computed(
    'model.@each.{authorId,createTime,moderationStatus}',
    'session.personId',
    'commentStartTime',
    'status',
  )
  get filteredComments() {
    return this.model.filter((comment) => {
      return (
        comment.get('moderationStatus') === this.status &&
        comment.get('author.id') !== this.session.get('personId') &&
        comment.get('createTime') >= this.commentStartTime
      );
    });
  }

  @tracked
  selectedComments = [];

  @computed('selectedComments.length', 'filteredComments.length')
  get allFilteredCommentsSelected() {
    // At least one must be selected in order for all to be selected, but
    // changing the timeframe changes the length without clearing selections.
    return (
      this.selectedComments.length &&
      this.selectedComments.length === this.filteredComments.length &&
      this.filteredComments.every((comment) =>
        this.selectedComments.includes(comment),
      )
    );
  }

  @action
  setStatus(newStatus) {
    // Reset selectedComments when changing the status filter, as we'll have a
    // new list of comments.
    this.status = newStatus;
    this.selectedComments = [];
  }

  @action
  isProfane(comment) {
    const filter = new Filter({ exclude: this.safeWords });
    return filter.isProfane(comment);
  }

  @action
  toggleSelected(entity) {
    if (this.selectedComments.includes(entity)) {
      this.selectedComments = this.selectedComments.filter(
        (comment) => comment !== entity,
      );
    } else {
      this.selectedComments = [...this.selectedComments, entity];
    }
  }

  @action
  toggleSelectAll() {
    if (this.selectedComments.length < this.filteredComments.length) {
      this.selectedComments = [...this.filteredComments];
    } else {
      this.selectedComments = [];
    }
  }

  @action
  viewPost(postId) {
    this.router.transitionTo('/home/community-notices/' + postId);
  }

  @action
  openCommentDialog(approveAllDialogStatus, declineAllDialogStatus) {
    this.approveAllCommentDialogOpen = approveAllDialogStatus;
    this.declineAllCommentDialogOpen = declineAllDialogStatus;
  }

  @action
  confirmCommentApproval(commentId) {
    this.heroApi.comments.approve(commentId).then(() => {
      this.toaster.success(`Comment has been successfully approved`);
    });
  }

  @action
  confirmCommentDecline(commentId) {
    this.heroApi.comments.decline(commentId).then(() => {
      this.toaster.success(`Comment has been successfully declined`);
    });
  }

  @action
  confirmAllCommentApproval() {
    let promises = [];
    this.selectedComments.forEach((element) => {
      promises.push(this.heroApi.comments.approve(element.id));
    });
    Promise.all(promises)
      .then(() => {
        this.toaster.success(
          `Selected comments have been successfully approved`,
        );
      })
      .catch(() => {
        this.toaster.error(`Error approving comments`);
      })
      .finally(() => {
        this.approveAllCommentDialogOpen = false;
        this.selectedComments = [];
      });
  }

  @action
  confirmAllCommentDecline() {
    let promises = [];
    this.selectedComments.forEach((element) => {
      promises.push(this.heroApi.comments.decline(element.id));
    });
    Promise.all(promises)
      .then(() => {
        this.toaster.success(
          `Selected comments have been successfully declined`,
        );
      })
      .catch(() => {
        this.toaster.error(`Error declining comments`);
      })
      .finally(() => {
        this.declineAllCommentDialogOpen = false;
        this.selectedComments = [];
      });
  }

  @action
  close() {
    this.approveAllCommentDialogOpen = false;
    this.declineAllCommentDialogOpen = false;
  }
}
